import React from "react";
import { Trophy } from "lucide-react";
import {
  formatCoin,
  getGameMinimumPlayers,
  getGameWinnerEntries,
  getGameWinnerName,
  getInitials,
  findUserParticipationEntry,
  getUserWinnerEntry,
  isUserWinner,
  sliceColorForIndex,
  wasUserInGame,
} from "../../utils/bondWheelHelpers";

const BondWheelStageResult = ({ game, userId }) => {
  if (!game) return null;

  if (game.status === "Cancelled") {
    const minimumPlayers = getGameMinimumPlayers(game);
    const count = game.activeParticipantCount ?? game.participantCount ?? 0;
    const userEntry = findUserParticipationEntry(game, userId);
    return (
      <section className="bplay-result-panel bplay-result-panel-cancelled">
        <header className="bplay-result-panel-head">
          <span className="bplay-result-panel-label">Result</span>
          <span className="bplay-result-panel-badge cancelled">Canceled</span>
        </header>
        <div className="bplay-result-panel-body">
          <p className="bplay-result-copy">
            Only {count} of {minimumPlayers} required players joined — everyone was refunded.
          </p>
          {wasUserInGame(game, userId) ? (
            <p className="bplay-result-refund">
              {formatCoin(userEntry?.entryAmount || game.entryAmount)} COIN refunded
            </p>
          ) : null}
        </div>
      </section>
    );
  }

  if (game.status !== "Settled") return null;

  const winnerEntries = getGameWinnerEntries(game);
  const multiWinner = winnerEntries.length > 1;
  const winnerName = getGameWinnerName(game) || "Winner";
  const isWin = isUserWinner(game, userId);
  const myWinnerEntries = winnerEntries.filter((entry) => {
    const entryUserId = String(entry.user?._id || entry.user || "");
    return userId && entryUserId === String(userId);
  });
  const myEntry = getUserWinnerEntry(game, userId);
  const netPayout =
    myWinnerEntries.length > 0
      ? myWinnerEntries.reduce((sum, entry) => sum + Number(entry.netPayout || 0), 0)
      : Number(myEntry?.netPayout ?? game.winnerNetPayout ?? 0);
  const wasIn = wasUserInGame(game, userId);
  const sliceIndex = Number.isFinite(game.winningSliceIndex) ? game.winningSliceIndex : 0;

  return (
    <section
      className={`bplay-result-panel ${isWin ? "bplay-result-panel-win" : wasIn ? "bplay-result-panel-loss" : ""}`}
    >
      <header className="bplay-result-panel-head">
        <span className="bplay-result-panel-label">Result</span>
        <span className="bplay-result-panel-badge settled">Settled</span>
      </header>

      <div className="bplay-result-panel-body">
        {isWin ? (
          <div className="bplay-result-hero">
            <span className="bplay-result-hero-icon">
              <Trophy size={18} />
            </span>
            <div className="bplay-result-hero-text">
              <span className="bplay-result-hero-kicker">
                {multiWinner ? "You won a share" : "You won"}
              </span>
              <strong className="bplay-result-hero-amount">{formatCoin(netPayout)} COIN</strong>
            </div>
          </div>
        ) : null}

        {multiWinner ? (
          <div className="bplay-result-winners-list">
            <span className="bplay-result-winner-label">{winnerEntries.length} winners</span>
            {winnerEntries.map((entry, idx) => {
              const name = entry.user?.name || "Player";
              const entryUserId = String(entry.user?._id || entry.user || "");
              const isYou = userId && entryUserId === String(userId);
              return (
                <div key={String(entry._id || idx)} className="bplay-result-winner-card">
                  <span
                    className="bplay-result-winner-avatar"
                    style={{ backgroundColor: sliceColorForIndex(entry.sliceIndex ?? idx) }}
                  >
                    {getInitials(name)}
                  </span>
                  <div className="bplay-result-winner-meta">
                    <span className="bplay-result-winner-label">Winner</span>
                    <span className="bplay-result-winner-name">
                      {name}
                      {isYou ? " (you)" : ""}
                    </span>
                  </div>
                  <div className="bplay-result-winner-payout">
                    <span className="bplay-result-winner-label">Net</span>
                    <strong>{formatCoin(entry.netPayout ?? 0)} COIN</strong>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bplay-result-winner-card">
            <span
              className="bplay-result-winner-avatar"
              style={{ backgroundColor: sliceColorForIndex(sliceIndex) }}
            >
              {getInitials(winnerName)}
            </span>
            <div className="bplay-result-winner-meta">
              <span className="bplay-result-winner-label">Winner</span>
              <span className="bplay-result-winner-name">{winnerName}</span>
            </div>
            <div className="bplay-result-winner-payout">
              <span className="bplay-result-winner-label">Won</span>
              <strong>{formatCoin(netPayout)} COIN</strong>
            </div>
          </div>
        )}

        {!isWin && wasIn && !multiWinner ? (
          <p className="bplay-result-copy">Better luck next time.</p>
        ) : null}
      </div>
    </section>
  );
};

export default BondWheelStageResult;
